import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { Observable, Subject } from 'rxjs';
import { Permission, User, UserType } from '../../app/core/model/user.model';

@Injectable()
export class FlamingoAuthService {
  private _token: string;
  private _localStorage = window.localStorage;
  private _sessionStorage = window.sessionStorage;
  private _storage = sessionStorage;
  private _storagePrefix = 'flamingo_';
  private _jwtHelper = new JwtHelperService();
  private _sessionEnded$ = new Subject<UserType | null>();
  private _unauthorizedHandled = false;

  constructor(
    private _fuseNavigationService: FuseNavigationService,
  ) {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (!event.key?.startsWith(this._storagePrefix) || event.newValue !== null) {
        return;
      }
      if (this._user || this._token) {
        const userType = this._user?.type ?? null;
        this._token = null;
        this._refreshToken = null;
        this._user = null;
        this._unauthorizedHandled = true;
        this._sessionEnded$.next(userType);
      }
    });
  }

  get sessionEnded$(): Observable<UserType | null> {
    return this._sessionEnded$.asObservable();
  }

  private _refreshToken: string;

  get refreshToken(): string {
    if (!this._refreshToken) {
      this._refreshToken = this._storage.getItem(this._storagePrefix + 'refresh_token');
    }
    return this._refreshToken;
  }

  set refreshToken(value: string) {
    this._storage.setItem(this._storagePrefix + 'refresh_token', value);
    this._refreshToken = null;
  }

  private _user: User;

  get user(): User {
    return this._user;
  }

  set user(value: User) {
    this._user = value;
  }

  public decodeJwtToken(token: string): any {
    return this._jwtHelper.decodeToken(token);
  }

  public rememberMe(rememberMe = false): void {
    this._storage = this._sessionStorage;
    if (rememberMe) {
      this._storage = this._localStorage;
    }
  }

  public getToken(): string {
    if (!this._token) {
      this._token = this._storage.getItem(this._storagePrefix + 'token');

      if (!this._token) {
        this._storage = this._localStorage;
        this._token = this._storage.getItem(this._storagePrefix + 'token');
      }
    }
    return this._token;
  }

  public setToken(token: string): void {
    this._storage.setItem(this._storagePrefix + 'token', token);
    this._refreshToken = null;
    this._unauthorizedHandled = false;
    this.updateUserFromToken();
  }

  public updateUserFromToken(): void {
    const decodedToken = this._jwtHelper.decodeToken(this.getToken());
    this.user = {
      id: decodedToken.id,
      email: decodedToken.email,
      company: decodedToken.company,
      type: decodedToken.type,
      createdAt: decodedToken.iat,
      expiredAt: decodedToken.exp,
      username: decodedToken.username,
      permissions: decodedToken.perm,
      partnerPermissions: decodedToken.pperm,
    } as User;
  }

  public resetToken(): void {
    this._storage.clear();
  }

  hasUserPermissions = (requiredPermissions: Permission | Permission[]): boolean => {
    const perm = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
    return this.user.permissions.some(p => [Permission.All, ...perm].includes(p));
  };

  can(type: UserType | UserType[]): boolean {
    if (!Array.isArray(type)) {
      type = [type];
    }
    return type.every(p => this.user?.type === p);
  }

  cannot(type: UserType | UserType[]): boolean {
    if (!Array.isArray(type)) {
      type = [type];
    }

    return !type.every(p => this.user?.type === p);
  }

  public logout(): void {
    const hadSession = !!(this._user || this._token || this.getToken());
    const userType = this._user?.type ?? null;
    this.resetToken();
    this._token = null;
    this._refreshToken = null;
    this._user = null;
    if (hadSession) {
      this._sessionEnded$.next(userType);
    }
  }

  public markUnauthorized(): void {
    if (this._unauthorizedHandled) {
      return;
    }
    this._unauthorizedHandled = true;
    this.logout();
  }

  public isLogged(): boolean {
    return !!this._user;
  }
}
